import React, { FormEvent, useCallback, useMemo, useState } from "react";
import { QuestionCourseInterface } from "../../domain/interface/question_course_interface";
import { useOutletContext, useParams } from "react-router-dom";
import { UserInterface } from "../../../../core/components/user/domain/interfaces/user_interface";
import { AnswerInterface } from "../../domain/interface/answer_interface";
import { getCurrentUserTheme } from "../../domain/utils/get_current_user_theme";
import { useTranslation } from "../../../../i18n/useTranslation";

interface StartQuizzQuestionProps {
    question: QuestionCourseInterface;
    color: string;
    index: number;
    questionsLength: number;
    answer?: AnswerInterface;
    isPending: boolean;
    onNext: (answer?: AnswerInterface) => void;
}

const StartQuizzQuestion: React.FC<StartQuizzQuestionProps> = ({ question, color, index, questionsLength, answer, isPending, onNext }) => {
    const { t } = useTranslation();
    const [ isFocused, setIsFocused ] = useState(false);
    const { themeId } = useParams();
    const { userId, course_theme, company } = useOutletContext<UserInterface>();
    const canFocus = useMemo(() => {
        const currentTheme = getCurrentUserTheme(course_theme, company?.id);
        return currentTheme === undefined;
    }, [course_theme, company?.id]);
    

    const handleSubmit = useCallback((e: FormEvent) => {
        e.preventDefault();
        if (isPending) return;
        const text = ((e.target as HTMLFormElement).elements[0] as HTMLInputElement | HTMLTextAreaElement).value;
        if (!text) {
            return setIsFocused(true);
        }
        onNext(answer?._id !== undefined ? undefined : {
          answer: text as string,
          _questionId: question._id,
          _themeId: themeId || '',
          _userId: userId,
        });
    }, [onNext, question._id, answer, isPending, themeId, userId]);

    return (
        <form
            style={{ borderColor: color }}
            className="border-2 p-4 rounded-2xl bg-white flex flex-col gap-5 items-center w-full max-w-200"
            onSubmit={handleSubmit}
        >
            <h2 className="font-medium text-center leading-snug">
                {t('common.question_progress',{ current: index + 1, total: questionsLength })}
            </h2>
            <p className="font-bold text-center text-sm leading-snug">
                {question.question}
            </p>
            <textarea
                defaultValue={answer?.answer}
                style={{
                    backgroundColor: '#D9D9D9',
                    border: isFocused
                      ? `1px solid ${color}`
                      : 'none',
                    outline: 'none',
                }}
                onFocus={() =>
                    canFocus &&
                    setIsFocused(true)
                }
                onBlur={() =>
                    canFocus &&
                    setIsFocused(false)
                }
                className="w-full max-sm:min-h-32 sm:min-h-50 md:min-h-60 xl:min-h-70 rounded-xl p-2 "
                required
                readOnly={answer?._id !== undefined || isPending}
            />
            <button
                type="submit"
                className="text-white py-2.25 px-11 rounded-xl cursor-pointer"
                style={{ backgroundColor: color }}
            >
                {isPending ? t('course.quiz.sending') : questionsLength - 1 === index ? t('common.button.finish') : t('common.button.next')}
            </button>
        </form>
    );
}

export default StartQuizzQuestion;